import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';

enum IntercomCommands
{
    BOOT = 'boot',

    SHOW = 'show',
    HIDE = 'hide',
    SHOW_MESSAGES = 'showMessages',
    SHOW_NEW_MESSAGE = 'showNewMessage',

    UPDATE = 'update',
    SHUTDOWN = 'shutdown',

    ON_HIDE = 'onHide',
    ON_SHOW = 'onShow',
    ON_UNREAD_COUNT_CHANGE = 'onUnreadCountChange',

    TRACK_EVENT = 'trackEvent',
    GET_VISITOR_ID = 'getVisitorId',
}

interface IntercomSettings
{
    // Messenger attributes
    app_id?:string;
    alignment?:string;
    custom_launcher_selector?:string;
    hide_default_launcher?:boolean;
    horizontal_padding?:number;
    session_duration?:number;
    vertical_padding?:number;
    action_color?:string;
    background_color?:string;

    // Data attributes
    email?:string;
    phone?:string;
    created_at?:number;
    name?:string;
    user_id?:string;
    user_hash?:string;
    unsubscribed_from_emails?:boolean;
    language_override?:string;
    utm_campaign?:string;
    utm_content?:string;
    utm_medium?:string;
    utm_source?:string;
    utm_term?:string;
    company?:{
        id:string | number;
        name:string;
        created_at?:number;
        plan?:string;
        monthly_spend?:number;
        user_count?:number;
        size?:number;
        website?:string;
        industry?:string;
    };
}

type IntercomCommand = 'boot'
    | 'shutdown'
    | 'update'
    | 'hide'
    | 'show'
    | 'showMessages'
    | 'showNewMessage'
    | 'onHide'
    | 'onShow'
    | 'onUnreadCountChange'
    | 'onActivatorClick'
    | 'trackEvent'
    | 'getVisitorId';

interface IntercomStatic
{
    (command:'boot', param:IntercomSettings):void;

    (command:'shutdown' | 'hide' | 'show' | 'showMessages'):void;

    (command:'update', param?:IntercomSettings):void;

    (command:'showNewMessage', param?:string):void;

    (command:'onHide' | 'onShow' | 'onActivatorClick', param?:() => void):void;

    (command:'trackEvent', tag?:string, metadata?:any):void;

    (command:'onUnreadCountChange', cb:(unreadCount:number) => void):void;

    (command:'getVisitorId'):string;

    (command:IntercomCommand, param1?:any, param2?:any):void;

    booted:boolean;
}

export class IntercomController
{
    private _isInitialized:boolean = false;

    public init():void
    {
        if( !ConfigUtil.useIntercom() || this.isInitialized() )
            return;

        // (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/'+INTERCOM_APP_ID;var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();

        // The original intercom code snippet is above in comments section.
        // The below script is slightly modified (aside from formatting)
        // We initialize intercom after window onload event is fired. So we did some changes in that regard.
        // IMPORTANT: in case of copying a new intercom script make sure to apply changes mentioned above.
        (window as any).intercomSettings = {app_id: ConfigUtil.intercomAppId()};
        (function()
        {
            var w = window as any;
            var ic = w.Intercom;
            if( typeof ic === 'function' )
            {
                ic('reattach_activator');
                ic('update', w.intercomSettings);
            }
            else
            {
                var d = document;
                var i = function()
                {
                    i.c(arguments);
                } as any;
                i.q = [];
                i.c = function(args)
                {
                    i.q.push(args);
                };
                w.Intercom = i;
                var l = function()
                {
                    var s = d.createElement('script');
                    s.type = 'text/javascript';
                    s.async = true;
                    s.src = 'https://widget.intercom.io/widget/' + ConfigUtil.intercomAppId();
                    var x = d.getElementsByTagName('script')[0];
                    x.parentNode.insertBefore(s, x);
                };

                l();
            }
        })();

        this._isInitialized = true;
    }

    public isInitialized():boolean
    {
        return this._isInitialized;
    }

    private _getApi():IntercomStatic | null
    {
        if( !this.isInitialized() )
            return null;

        return (window as any).Intercom as IntercomStatic;
    }

    private _api(method:IntercomCommand, settings?:IntercomSettings | string, metadata?:any):void
    {
        const intercomAPI:IntercomStatic | null = this._getApi();

        if( !intercomAPI )
        {
            // console.log('%c intercomAPI =', 'background:#f00;color:#ff0;', intercomAPI);
            return;
        }

        // console.log('%c IntercomController =', 'background:#0f0;color:#000;', method, settings, metadata);
        intercomAPI(method, settings, metadata);
    }

    public show():void
    {
        this._api(IntercomCommands.SHOW);
    }

    public boot(settings?:IntercomSettings):void
    {
        // console.log('%c IntercomController BOOT =', 'background:#0f0;color:#000;', settings);
        if( settings && settings.name && settings.email )
        {
            const created_at = Math.floor(Date.now() / 1000);
            this._api(IntercomCommands.BOOT, {...settings, created_at});
        }
        else
        {
            this._api(IntercomCommands.BOOT, settings);
        }
    }

    public shutdown():void
    {
        // console.log('%c IntercomController SHUTDOWN =', 'background:#0f0;color:#000;');
        this._api(IntercomCommands.SHUTDOWN);
    }

    public update(settings:IntercomSettings):void
    {
        // console.log('%c IntercomController UPDATE =', 'background:#0f0;color:#000;', settings);
        if( settings && settings.name && settings.email )
        {
            const created_at = Math.floor(Date.now() / 1000);
            this._api(IntercomCommands.UPDATE, {...settings, created_at});
        }
        else
        {
            this._api(IntercomCommands.UPDATE, settings);
        }
    }

    public trackEvent(tag:AnalyticsEventActions, metadata:any):void
    {
        // console.log('%c IntercomController TRACK_EVENT =', 'background:#0f0;color:#000;', tag, metadata);
        this._api(IntercomCommands.TRACK_EVENT, tag, metadata);
    }

    // public boot()
    // {
    // return;

    // const {userSession} = rootState;
    //
    // if( userSession && userSession.currentUser )
    //     this._boot(userSession.currentUser);
    // else
    //     this._boot(null);
    // }

    // private _boot(user:User|null)
    // {
    //     const envType = 'dev';
    //
    //     if( user )
    //     {
    //         (window as any).intercomSettings =
    //         {
    //             app_id: intercomAppId,
    //             name: user.fullName,     // Full name
    //             // email: user.username,    // Email address
    //             env_type: envType
    //             // created_at: <%= current_user.created_at.to_i %> // Signup Date
    //         };
    //
    //     }
    //
    //     (window as any).Intercom("boot", {
    //         app_id: intercomAppId
    //     });
    // }

}
